import { useCallback, useMemo, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import Svg, { Circle, G, Line, Path, Rect, Text as SvgText } from "react-native-svg";
import { useUnistyles, StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { isWeb } from "@src/utils/web-helpers";
import type { RoadSenseTrend } from "@visioni/a3s-reports/client-messages";
import { isSiteLocalWeekend } from "../filters/traffic-filters";

interface TrafficTrendChartProps {
  trend: RoadSenseTrend;
  /**
   * Optional fixed pixel height for the SVG. Omit (the default) to fill the
   * parent — the chart then measures its box and matches the viewBox aspect to
   * it, so it fills the pane without letterboxing.
   */
  height?: number;
  /**
   * Whether to paint weekend bands. The caller turns this off when the series
   * has been aggregated coarser than daily (a week/month/year bucket spans
   * weekends, so a single shaded point is meaningless). Defaults to `true`.
   */
  showWeekendShading?: boolean;
  /**
   * Whether the movements being plotted are an explicit user-picked subset
   * (rather than the full "All" set). Only the label of the dashed sum line
   * depends on it — "Sum" when a subset is selected (it sums just those lines),
   * "All" when every movement is shown. Defaults to `false` ("All"). Irrelevant
   * when there's a single movement or none (no dashed line is drawn).
   */
  movementsAreSubset?: boolean;
}

const VIEW_W = 760;
/**
 * Fallback viewBox dimensions (used only before first layout). At runtime the
 * viewBox is set to the measured pixel box so 1 userspace unit = 1 CSS pixel —
 * the chart fills its pane and keeps fonts/strokes at their intended pixel size
 * regardless of pane width. See `viewW`/`viewH` in the component.
 */
const VIEW_H = 260;
/** Left padding holds the Y-axis tick labels (widest expected ~"99,999"). */
const PAD_X_LEFT = 52;
const PAD_X_RIGHT = 12;
const PAD_Y_TOP = 16;
const PAD_Y_BOTTOM = 28;
const GRID_LINES = 5;
const Y_LABEL_RIGHT_GAP = 6;
/**
 * Cap how many X-axis labels we render so a month/quarter of daily points
 * doesn't overprint the axis. We thin to roughly this many evenly-spaced
 * ticks (always keeping the first and last).
 */
const MAX_X_LABELS = 8;

/**
 * RoadSense traffic-flow trend chart — a **RoadSense-owned** multi-series line
 * chart, built fresh on the codebase's `react-native-svg` charting technique
 * (the WasteSense `DailyActivityTrend` was a reference for *technique only*;
 * this shares no code with it).
 *
 * Plots either the combined `total` line, or — when the filtered trend carries
 * movements — one line per movement (Northbound, Southbound, …) so flows can be
 * compared, plus a dashed sum line over them (only when there's more than one
 * movement, so it isn't a redundant duplicate). That sum line is labelled "All"
 * when every movement is shown and "Sum" when the user has narrowed to an
 * explicit subset (see `movementsAreSubset`) — so it always reads as "the sum of
 * exactly the lines you're looking at". Classification is
 * summed upstream into each line's total, so this component never sees per-class
 * series. Series are straight polylines (not smoothed) so a single-day spike
 * can't pull the curve below 0 or above the max — the line's extrema are
 * guaranteed to sit at sample points. Painted with `react-native-svg` so the
 * same code renders on web and native; the hover interaction is web-only (no
 * pointer events fire on the native placeholder path).
 *
 * Weekend days (derived from `trend.instants`) get a faint shaded band behind
 * the plot — the example exports use this to make the weekday/weekend rhythm of
 * a traffic series legible at a glance. Shading is suppressed once the series is
 * aggregated coarser than daily (week/month/year), where a "weekend" no longer
 * maps to a single point.
 *
 * The chart renders whatever series the (already filtered/aggregated) trend
 * carries; granularity, date-range, movement, and classification filtering
 * happen upstream in `RoadSenseReportView` so this component stays a pure
 * renderer.
 */
export function TrafficTrendChart({
  trend,
  height,
  showWeekendShading = true,
  movementsAreSubset = false,
}: TrafficTrendChartProps) {
  const { theme } = useUnistyles();

  const gridColor = theme.colors.borderCard;
  const labelColor = theme.colors.textMuted;
  const guideColor = theme.colors.borderCard;
  // Weekend bands are a low-emphasis backdrop — the warning token at low alpha
  // matches the soft pink shading in the reference exports without hardcoding a
  // hex, and reads in both light and dark themes.
  const weekendBandColor = theme.colors.warning[600];

  // One line per movement when the filtered trend carries them (so flows can be
  // compared side by side), plus a dashed sum line over them so the combined
  // figure reads at a glance. It sums exactly the movements being shown, so it's
  // "All" when every movement is plotted and "Sum" when the user has narrowed to
  // a subset. With a single movement (or none) the payload is just a single
  // combined "Total" line — no redundant sum line.
  // Colours cycle through a small colour-blind-distinguishable palette.
  const seriesDefs = useMemo<readonly SeriesDef[]>(() => {
    const palette = [theme.colors.teal[500], theme.colors.blue[500], theme.colors.warning[600], theme.colors.grey[400]];
    const movements = trend.movements ?? [];
    if (movements.length > 0) {
      const lines: SeriesDef[] = movements.map((m, i) => ({
        key: m.id,
        label: m.label,
        color: palette[i % palette.length] ?? theme.colors.teal[500],
        data: m.total_series,
      }));
      if (movements.length > 1) {
        lines.unshift({
          key: "all",
          label: movementsAreSubset ? "Sum" : "All",
          color: theme.colors.textMuted,
          data: sumSeries(movements.map((m) => m.total_series)),
          dashed: true,
        });
      }
      return lines;
    }
    return [{ key: "total", label: "Total", color: theme.colors.teal[500], data: trend.total_series }];
  }, [trend, theme, movementsAreSubset]);

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height: h } = event.nativeEvent.layout;
    setContainerSize({ width, height: h });
  }, []);

  const containerWidth = containerSize.width;

  // Use the *measured* pixel box as the SVG's viewBox (1 userspace unit = 1 CSS
  // pixel). That fills the pane top-to-bottom without letterboxing, and — unlike
  // a fixed wide viewBox scaled to fit — keeps font sizes and stroke widths at
  // their intended pixel size instead of shrinking with the width. Falls back to
  // the design constants before first layout.
  const viewW = containerSize.width > 0 ? containerSize.width : VIEW_W;
  const viewH = containerSize.height > 0 ? containerSize.height : VIEW_H;

  const xAxisLabelY = viewH - 6;
  const plotBottom = viewH - PAD_Y_BOTTOM;

  const { plotted, gridYs, gridValues, xs, xLabelIndices, weekendBands } = useMemo(
    () => buildPlot(trend, seriesDefs, viewW, viewH),
    [trend, seriesDefs, viewW, viewH],
  );

  const indexFromContainerX = useCallback(
    (containerX: number): number | null => {
      if (containerWidth <= 0 || xs.length === 0) return null;
      const scale = viewW / containerWidth;
      const svgX = containerX * scale;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < xs.length; i += 1) {
        const dist = Math.abs(svgX - (xs[i] ?? 0));
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    },
    [containerWidth, viewW, xs],
  );

  const handlePointer = useCallback(
    (event: { nativeEvent: { offsetX?: number; locationX?: number } }) => {
      const offsetX = event.nativeEvent.offsetX ?? event.nativeEvent.locationX ?? 0;
      setHoverIndex(indexFromContainerX(offsetX));
    },
    [indexFromContainerX],
  );

  const clearHover = useCallback(() => setHoverIndex(null), []);

  const interactionProps =
    isWeb ?
      ({
        onMouseMove: handlePointer,
        onMouseLeave: clearHover,
      } as object)
    : {};

  const activeX = hoverIndex !== null ? (xs[hoverIndex] ?? 0) : 0;
  const tooltip =
    hoverIndex !== null && containerWidth > 0 ? buildTooltipPlacement({ svgX: activeX, containerWidth, viewW }) : null;

  return (
    <View gap="sm" flex={1}>
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        Vehicle count trend
      </Text>
      <View
        onLayout={handleLayout}
        style={styles.chartSurface}
        onTouchStart={isWeb ? undefined : handlePointer}
        onTouchMove={isWeb ? undefined : handlePointer}
        onTouchEnd={isWeb ? undefined : clearHover}
        {...interactionProps}
      >
        <Svg
          viewBox={`0 0 ${viewW} ${viewH}`}
          width="100%"
          height={height ?? "100%"}
          preserveAspectRatio="xMidYMid meet"
        >
          <G>
            {showWeekendShading ?
              weekendBands.map((band, i) => (
                <Rect
                  key={`weekend-${i}`}
                  // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
                  x={band.x}
                  // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
                  y={PAD_Y_TOP}
                  width={band.width}
                  height={viewH - PAD_Y_TOP - PAD_Y_BOTTOM}
                  fill={weekendBandColor}
                  opacity={0.1}
                />
              ))
            : null}
            {gridYs.map((y, i) => (
              <Line
                key={`grid-${i}`}
                x1={PAD_X_LEFT}
                y1={y}
                x2={viewW - PAD_X_RIGHT}
                y2={y}
                stroke={gridColor}
                strokeWidth={1}
                strokeDasharray="3 4"
              />
            ))}
            {gridYs.map((y, i) => {
              const value = gridValues[i] ?? 0;
              const isTop = i === 0;
              const isBottom = i === gridValues.length - 1;
              if (!isTop && !isBottom) {
                const top = gridValues[0] ?? 0;
                const bottom = gridValues[gridValues.length - 1] ?? 0;
                if (value === top || value === bottom) return null;
                const prev = gridValues[i - 1] ?? null;
                if (prev !== null && prev === value) return null;
              }
              return (
                <SvgText
                  key={`y-label-${i}`}
                  // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
                  x={PAD_X_LEFT - Y_LABEL_RIGHT_GAP}
                  // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
                  y={y + 3}
                  fontSize={11}
                  textAnchor="end"
                  fill={labelColor}
                >
                  {value.toLocaleString()}
                </SvgText>
              );
            })}
          </G>

          {hoverIndex !== null ?
            <Line x1={activeX} y1={PAD_Y_TOP} x2={activeX} y2={plotBottom} stroke={guideColor} strokeWidth={1} />
          : null}

          {plotted.map((s) => (
            <Path
              key={`path-${s.key}`}
              d={s.path}
              fill="none"
              stroke={s.color}
              strokeWidth={2.6}
              strokeDasharray={s.dashed ? "6 5" : undefined}
            />
          ))}

          {/* Dot markers only on the real movement lines — the dashed sum line
              is a reference line, so markers would imply it's its own data series. */}
          {plotted.map((s) =>
            s.dashed ? null : (
              s.points.map((p, i) => <Circle key={`pt-${s.key}-${i}`} cx={p.x} cy={p.y} r={3.0} fill={s.color} />)
            ),
          )}

          {hoverIndex !== null ?
            plotted.map((s) => {
              const p = s.points[hoverIndex];
              if (!p) return null;
              return (
                <Circle key={`active-${s.key}`} cx={p.x} cy={p.y} r={6} fill="none" stroke={s.color} strokeWidth={2} />
              );
            })
          : null}

          {xLabelIndices.map((idx, i) => {
            // First/last ticks sit on the plot's left/right edges; a centred
            // anchor would push half the text past the viewBox and clip it
            // (e.g. the trailing date losing its month). Anchor the ends inward.
            const anchor =
              i === 0 ? "start"
              : i === xLabelIndices.length - 1 ? "end"
              : "middle";
            return (
              <SvgText
                key={`label-${idx}`}
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
                x={xs[idx]}
                // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
                y={xAxisLabelY}
                fontSize={11}
                textAnchor={anchor}
                fill={labelColor}
              >
                {trend.labels[idx]}
              </SvgText>
            );
          })}
        </Svg>

        {hoverIndex !== null && tooltip ?
          <View pointerEvents="none" style={[styles.tooltip, { left: tooltip.left, top: tooltip.top }]}>
            <Text textStyle="xs/semibold" color="text">
              {trend.labels[hoverIndex]}
            </Text>
            {plotted.map((s) => (
              <Text key={`tt-${s.key}`} textStyle="xs/regular" color="textMuted">
                {s.label}: {(s.data[hoverIndex] ?? 0).toLocaleString()}
              </Text>
            ))}
          </View>
        : null}
      </View>

      <View flexDirection="row" gap="md" alignItems="center" flexWrap="wrap" px="xs">
        {seriesDefs.map((s) => (
          <LegendKey key={`legend-${s.key}`} color={s.color} label={s.label} dashed={s.dashed} />
        ))}
        {showWeekendShading && weekendBands.length > 0 ?
          <LegendKey color={weekendBandColor} label="Weekend" faded />
        : null}
      </View>
    </View>
  );
}

const TOOLTIP_WIDTH = 184;
const TOOLTIP_GAP = 12;

const styles = StyleSheet.create((theme) => ({
  chartSurface: {
    position: "relative",
    width: "100%",
    flex: 1,
  },
  // Only the themed/static styling lives here; `left`/`top` are computed per
  // hover position and merged in inline at the call site.
  tooltip: {
    position: "absolute",
    maxWidth: TOOLTIP_WIDTH,
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderCard,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  // `backgroundColor` is the per-series colour, merged in inline.
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  // Dashed line key for the movement sum series ("All" / "Sum") — a short rule
  // with a dashed top border so the swatch mirrors how the line is drawn.
  // `borderColor` inline.
  legendDash: {
    width: 14,
    height: 0,
    borderTopWidth: 2,
    borderStyle: "dashed",
  },
  // Weekend-band key swatch — matches the 0.1 opacity of the painted band.
  legendSwatchFaded: {
    opacity: 0.1,
  },
}));

interface LegendKeyProps {
  color: string;
  label: string;
  /** Render the swatch at low opacity to match a shaded band (weekend key). */
  faded?: boolean;
  /** Render a dashed line swatch instead of a solid block (the movement sum line). */
  dashed?: boolean;
}

function LegendKey({ color, label, faded = false, dashed = false }: LegendKeyProps) {
  return (
    <View flexDirection="row" alignItems="center" gap="xs">
      {dashed ?
        <View style={[styles.legendDash, { borderColor: color }]} />
      : <View style={[styles.legendSwatch, { backgroundColor: color }, faded ? styles.legendSwatchFaded : null]} />}
      <Text textStyle="xs/regular" color="textMuted">
        {label}
      </Text>
    </View>
  );
}

interface SeriesDef {
  key: string;
  label: string;
  color: string;
  data: readonly number[];
  /** Draw as a dashed line with no point markers (the movement sum reference). */
  dashed?: boolean;
}

interface PlotPoint {
  x: number;
  y: number;
}

interface PlottedSeries extends SeriesDef {
  path: string;
  points: readonly PlotPoint[];
}

interface WeekendBand {
  /** Left edge of the band in viewBox X units. */
  x: number;
  width: number;
}

interface PlotData {
  plotted: readonly PlottedSeries[];
  gridYs: readonly number[];
  /** Y-axis tick values aligned with `gridYs`; index 0 is `vmax`, last is 0. */
  gridValues: readonly number[];
  xs: readonly number[];
  /** Indices into `labels`/`xs` for the (thinned) X-axis ticks we render. */
  xLabelIndices: readonly number[];
  /** Shaded vertical bands marking Saturday/Sunday runs. */
  weekendBands: readonly WeekendBand[];
}

function buildPlot(trend: RoadSenseTrend, seriesDefs: readonly SeriesDef[], viewW: number, viewH: number): PlotData {
  const innerW = viewW - PAD_X_LEFT - PAD_X_RIGHT;
  const innerH = viewH - PAD_Y_TOP - PAD_Y_BOTTOM;
  const n = trend.labels.length;
  const stepX = n > 1 ? innerW / (n - 1) : 0;

  const xs: number[] = [];
  for (let i = 0; i < n; i += 1) xs.push(PAD_X_LEFT + stepX * i);

  // Always >=1 so a flat-zero series renders a baseline rather than NaN.
  const allValues = seriesDefs.flatMap((s) => s.data);
  const vmax = Math.max(1, ...allValues);

  const yFor = (v: number): number => {
    const ratio = vmax > 0 ? v / vmax : 0;
    return PAD_Y_TOP + innerH * (1 - ratio);
  };

  const plotted: PlottedSeries[] = seriesDefs.map((s) => {
    const points = s.data.map((v, i) => ({ x: xs[i] ?? 0, y: yFor(v) }));
    return { ...s, points, path: linearPath(points) };
  });

  const gridYs: number[] = [];
  const gridValues: number[] = [];
  for (let i = 0; i < GRID_LINES; i += 1) {
    const ratio = i / (GRID_LINES - 1);
    gridYs.push(PAD_Y_TOP + innerH * ratio);
    gridValues.push(i === GRID_LINES - 1 ? 0 : Math.round(vmax * (1 - ratio)));
  }

  return {
    plotted,
    gridYs,
    gridValues,
    xs,
    xLabelIndices: thinLabelIndices(n),
    weekendBands: buildWeekendBands(trend.instants, xs, stepX, viewW),
  };
}

/**
 * Build shaded bands over weekend (Sat/Sun) points. Each band spans half a
 * step on either side of its weekend points so it reads as a vertical column
 * centred on the day(s), clamped to the plot area. Runs of consecutive weekend
 * points merge into one band — so a Sat+Sun pair (daily series), or every
 * hourly point across a weekend (sub-day series), collapse into a
 * single column. Returns `[]` when `instants` is missing or misaligned with
 * `xs`, so the chart simply draws no shading rather than guessing.
 */
function buildWeekendBands(
  instants: readonly string[],
  xs: readonly number[],
  stepX: number,
  viewW: number,
): WeekendBand[] {
  if (instants.length === 0 || instants.length !== xs.length) return [];
  const half = stepX > 0 ? stepX / 2 : 0;
  const leftBound = PAD_X_LEFT;
  const rightBound = viewW - PAD_X_RIGHT;

  const bands: WeekendBand[] = [];
  let runStart: number | null = null;
  let runEnd = 0;

  const flush = () => {
    if (runStart === null) return;
    const x = Math.max(leftBound, runStart - half);
    const right = Math.min(rightBound, runEnd + half);
    bands.push({ x, width: Math.max(0, right - x) });
    runStart = null;
  };

  for (let i = 0; i < instants.length; i += 1) {
    if (isSiteLocalWeekend(instants[i])) {
      const x = xs[i] ?? 0;
      if (runStart === null) runStart = x;
      runEnd = x;
    } else {
      flush();
    }
  }
  flush();
  return bands;
}

/**
 * Choose evenly-spaced label indices, always including the first and last, so
 * a long daily series doesn't overprint the X axis. With `n <= MAX_X_LABELS`
 * every point gets a label.
 */
function thinLabelIndices(n: number): number[] {
  if (n <= 0) return [];
  if (n <= MAX_X_LABELS) return Array.from({ length: n }, (_, i) => i);
  const step = (n - 1) / (MAX_X_LABELS - 1);
  const indices = new Set<number>();
  for (let i = 0; i < MAX_X_LABELS; i += 1) indices.add(Math.round(i * step));
  return [...indices].sort((a, b) => a - b);
}

interface TooltipPlacementInput {
  svgX: number;
  containerWidth: number;
  viewW: number;
}

interface TooltipPlacement {
  left: number;
  top: number;
}

/**
 * Place the tooltip near the active point in container-pixel space, flipping
 * left/right of the point so it never spills outside the chart.
 */
function buildTooltipPlacement({ svgX, containerWidth, viewW }: TooltipPlacementInput): TooltipPlacement {
  const scale = viewW > 0 ? containerWidth / viewW : 1;
  const pointPx = svgX * scale;
  const rightEdgeIfRight = pointPx + TOOLTIP_GAP + TOOLTIP_WIDTH;
  const fitsRight = rightEdgeIfRight <= containerWidth - 4;
  const left = fitsRight ? pointPx + TOOLTIP_GAP : Math.max(4, pointPx - TOOLTIP_GAP - TOOLTIP_WIDTH);
  return { left, top: 8 };
}

/** Day-by-day sum of parallel series (the dashed line = the plotted movements added up). */
function sumSeries(series: readonly (readonly number[])[]): number[] {
  const n = series.reduce((max, s) => Math.max(max, s.length), 0);
  const out = new Array<number>(n).fill(0);
  for (const s of series) {
    for (let i = 0; i < s.length; i += 1) out[i] = (out[i] ?? 0) + (s[i] ?? 0);
  }
  return out;
}

/** Straight polyline through `points` — `M` to first, `L` to each subsequent. */
function linearPath(points: readonly PlotPoint[]): string {
  if (points.length === 0) return "";
  let d = `M ${points[0]!.x.toFixed(2)} ${points[0]!.y.toFixed(2)}`;
  for (let i = 1; i < points.length; i += 1) {
    const p = points[i]!;
    d += ` L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  }
  return d;
}
